import type { EOrderDirectionType } from '~/core/gemxql/types/order-by';
import type { IPickPageMode, IPickPageSource } from '../../types';

import { usePickPageCollectionSource } from './usePickPageCollectionSource';
import { usePickPageBlogPostSource } from './usePickPageBlogPostSource';
import { usePickPageProductSource } from './usePickPageProductSource';
import { usePickPageLandingSource } from './usePickPageLandingSource';
import { usePickPageMergedSource } from './usePickPageMergedSource';
import { usePickPageHomeSource } from './usePickPageHomeSource';
import { toPickPageSearchIntent } from '../../utils';
import { useShopifyAsset } from '~/modules/shopify';
import { useQueryThemePages } from '~/modules/apps';
import { toTemplateAssetKeys } from '~/utils';
import { IPickPageType } from '../../types';
import { useMemo } from 'react';

interface IUsePickPageSourceParams {
  /** What the modal picks — read by the product listing, whose statuses depend on it. */
  mode: IPickPageMode;
  /** Page types the mode offers, in the order a merged search groups them. */
  types: IPickPageType[];
  /** The page type on show — the active tab, or the mode's only type. */
  type: IPickPageType;
  searchValue: string;
  sort: EOrderDirectionType;
  isControl: boolean;
  /** A search spanning every page type, which the type tabs give way to. */
  isSearchAll: boolean;
}

/**
 * Resolves the list on show. Every source is mounted so the hook order stays stable across a tab
 * switch; only the active tab's fetches, or all of them at once while a merged search runs.
 */
export const usePickPageSource = ({
  mode,
  types,
  type,
  searchValue,
  sort,
  isControl,
  isSearchAll,
}: IUsePickPageSourceParams): IPickPageSource => {
  // A search naming a page type — `/products/gift-card`, or `/pages/` for no page in particular —
  // covers that type alone, so the other sources sit out even though the search spans them all.
  const searchType = toPickPageSearchIntent(searchValue).type;

  // Read here rather than in each source: every source resolves its rows against the same theme, and
  // the hook stringifies the whole asset store on each render — once for five sources, not five times.
  const { assets, isFetching: isShopifyAssetFetching, refetch: refetchAssets } = useShopifyAsset();
  const templateAssetKeys = useMemo(() => toTemplateAssetKeys(assets), [assets]);
  // Only the homepage needs these: it has no record to read a template off, so whichever theme page
  // is default answers for it. Same query the template listing runs, so a store that already loaded
  // them pays nothing here.
  const { themePages } = useQueryThemePages({ assets });

  const isSourceEnabled = (sourceType: IPickPageType): boolean => {
    if (!isSearchAll) return type === sourceType;

    return !searchType || searchType === sourceType;
  };

  const sourceByType: Record<IPickPageType, IPickPageSource> = {
    [IPickPageType.HOME]: usePickPageHomeSource({
      searchValue,
      isEnabled: isSourceEnabled(IPickPageType.HOME),
      templateAssetKeys,
      themePages,
    }),
    [IPickPageType.LANDING]: usePickPageLandingSource({
      searchValue,
      isEnabled: isSourceEnabled(IPickPageType.LANDING),
      templateAssetKeys,
    }),
    [IPickPageType.PRODUCT]: usePickPageProductSource({
      searchValue,
      isEnabled: isSourceEnabled(IPickPageType.PRODUCT),
      templateAssetKeys,
      sort,
      mode,
      isControl,
    }),
    [IPickPageType.COLLECTION]: usePickPageCollectionSource({
      searchValue,
      isEnabled: isSourceEnabled(IPickPageType.COLLECTION),
      templateAssetKeys,
    }),
    [IPickPageType.BLOG_POST]: usePickPageBlogPostSource({
      searchValue,
      isEnabled: isSourceEnabled(IPickPageType.BLOG_POST),
      templateAssetKeys,
    }),
  };

  const mergedSource = usePickPageMergedSource(types.map((pageType) => sourceByType[pageType]));
  const source = isSearchAll ? mergedSource : sourceByType[type];

  // The templates load on their own query, so the list is only settled once they are in — and a
  // refresh has to pull them again, or a template deleted since would still read as the one rendering.
  return {
    ...source,
    isLoading: source.isLoading || isShopifyAssetFetching,
    handleRefresh: () => Promise.all([source.handleRefresh(), refetchAssets()]),
  };
};
